import React, { Component } from 'react';
import { Alert } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import RouterPropTypes from 'react-router-prop-types';
import classNames from 'classnames';

import Paths from '../../../types/Paths';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import ButtonLink from '../../common/ButtonLink';
import ProductStore from '../../../store/ProductStore';
import ProductClass from '../../../types/ProductClass';
import { calculateNearestProductQuantityWithQuantityStep } from '../../../util/number';
import RouteService from '../../../services/RouteService';
import Analytics from '../../../analytics/Analytics';
import WishlistStore from '../../../store/WishlistStore';
import {
  createWishlistItemFromProduct,
  getWishlistProductId,
} from '../../../models/WishlistProduct';

@observer
export class ProductAddToWishList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      productAddedBanner: false,
      alertVisible: false,
    };

    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.onAlertDismiss = this.onAlertDismiss.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.setState({ alertVisible: false });
    }
  }

  addProductToWishlist = () => {
    const { product, quantity } = this.props;
    const newQuantity = calculateNearestProductQuantityWithQuantityStep(
      quantity,
      this.getQuantityStep(product)
    );
    this.findProduct(product, Math.ceil(newQuantity));
    this.showProductAddedToWishListBanner();
  };

  findProduct = (product, newQuantity) => {
    const { wishlistStore } = this.props;
    const productId = getWishlistProductId(product, this.props.activeProductId);
    const wishListProduct = wishlistStore.getItemById(productId);
    if (wishListProduct) {
      const productListProduct = this.initializeProductListProduct(
        product,
        newQuantity
      );
      this.updateProduct(wishListProduct, newQuantity, productListProduct);
      this.resetProductVariationChoices();
    } else {
      this.addProduct(product, newQuantity);
      this.resetProductVariationChoices();
    }
  };

  updateProduct = (productFound, newQuantity, productListProduct) => {
    const { analytics, accountStore, product, wishlistStore } = this.props;
    const updatedProduct = {
      ...productFound,
      ...productListProduct,
      quantity:
        newQuantity === 1 ? productFound.quantity + newQuantity : newQuantity,
    };

    wishlistStore.addOrUpdateItem(updatedProduct);
    const totalValue =
      product.getPrice(accountStore.showPricesWithTax) * newQuantity;

    analytics.addToWishlist(
      [
        {
          product,
          quantity: newQuantity,
          activeProductId: productFound.id,
        },
      ],
      Number(totalValue.toFixed(2))
    );
  };

  addProduct = (wishListProduct, newQuantity) => {
    const { analytics, accountStore, product, wishlistStore } = this.props;
    const productListProduct = this.initializeProductListProduct(
      wishListProduct,
      newQuantity
    );
    const totalValue =
      product.getPrice(accountStore.showPricesWithTax) * newQuantity;

    wishlistStore.addOrUpdateItem(productListProduct);

    analytics.addToWishlist(
      [
        {
          product,
          quantity: newQuantity,
          activeProductId: productListProduct.id,
        },
      ],
      Number(totalValue.toFixed(2))
    );
  };

  initializeProductListProduct = (product, quantity) => {
    const { productStore, routeService } = this.props;
    this.setVariationIfMultiProduct(productStore, product);
    return createWishlistItemFromProduct(
      product,
      quantity,
      routeService,
      this.resetVariationsIfProductIsNormal(productStore, product),
      this.props.activeProductId
    );
  };

  setVariationIfMultiProduct = (productStore, product) => {
    if (product.class === ProductClass.MULTI_CHILD) {
      productStore.setProductVariationColumn(`${product.model}`);
    }
  };

  resetVariationsIfProductIsNormal = (productStore, product) => {
    if (product.class === ProductClass.SINGLE) {
      this.resetProductVariationChoices();
      return this.getProductVariations(productStore);
    } else {
      return this.getProductVariations(productStore);
    }
  };

  resetProductVariationChoices = () => {
    const { productStore } = this.props;
    productStore.setProductVariationColumn(null);
    productStore.setProductVariationRow(null);
  };

  getProductVariations = (productStore) => {
    const variations = [];
    const productVariationColumn = productStore.getProductVariationColumn();
    productVariationColumn && variations.push(productVariationColumn);
    const productVariationRow = productStore.getProductVariationRow();
    productVariationRow && variations.push(productVariationRow);
    return variations;
  };

  getQuantityStep = (product) => {
    return product && product.sellInPackage ? product.package_size : 1;
  };

  showProductAddedToWishListBanner = () => {
    this.setState({
      productAddedBanner: true,
      alertVisible: true,
    });
  };

  onAlertDismiss = () => {
    this.setState({ alertVisible: false });
  };

  handleButtonClick = (e) => {
    if (this.props.disabled) {
      e.preventDefault();
      return null;
    }

    this.addProductToWishlist();
  };

  addToWishListButton = () => {
    return (
      <ButtonLink
        color="primary"
        className={classNames('ProductAddToWishList__button', {
          'ProductAddToWishList__button--disabled': this.props.disabled,
        })}
        onClick={this.handleButtonClick.bind(this)}
      >
        <FormattedMessage
          id="product.AddWishListTitle"
          defaultMessage="Add to wishlist"
        />
      </ButtonLink>
    );
  };

  productAddedBanner = () => {
    if (this.state.productAddedBanner) {
      return (
        <Alert
          className="ProductAddToWishList__product-added-banner"
          color="secondary"
          isOpen={this.state.alertVisible}
        >
          <FormattedMessage
            id="product.ProductAddedToWishlist"
            defaultMessage={`Product added to wishlist. You can view it {link}.`}
            values={{
              link: (
                <ButtonLink onClick={this.navigateToWishList}>
                  <FormattedMessage
                    id="product.ProductAddedToWishlist_here"
                    defaultMessage="here"
                  />
                </ButtonLink>
              ),
            }}
          />
        </Alert>
      );
    }
  };

  navigateToWishList = () => {
    this.props.history.push(Paths.WishList);
  };

  render() {
    return (
      <div className="ProductAddToWishList">
        {this.addToWishListButton()}
        {this.productAddedBanner()}
      </div>
    );
  }
}

ProductAddToWishList.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  history: RouterPropTypes.history.isRequired,
  product: PropTypes.object.isRequired,
  activeProductId: PropTypes.string,
  disabled: PropTypes.bool,
  wishlistStore: modelOf(WishlistStore).isRequired,
};

export default withRouter(
  inject(
    'accountStore',
    'productStore',
    'routeService',
    'analytics',
    'wishlistStore'
  )(ProductAddToWishList)
);
